import React, { useState, useEffect, useRef, useCallback } from 'react';
import { neonQuery, neonRawSelect, bcProxy } from '../services/api';
import ListStatus from '../components/ListStatus';
import { getCurrentUser, getAccessToken } from '../services/auth';
import jimmyImg from '../assets/Jimmy.png';
import { toTitleCase } from '../utils/format';
import VipRewardsModal from '../components/VipRewardsModal';
import VipBadge from '../components/VipBadge';
import { ChevronDown, ChevronUp, Phone, MessageSquare, Mail, Play, Pause, RefreshCw, MapPin, CreditCard, Trash2, Edit2, Check, X as XIcon } from 'lucide-react';

const US_STATES = [['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']];
const stateAbbr = (s) => { if (!s || s.length <= 2) return (s || '').toUpperCase(); const match = US_STATES.find(([, name]) => name.toLowerCase() === s.toLowerCase()); return match ? match[0] : s.toUpperCase(); };

function downloadCsv(rows) {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const escape = v => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `jimmy-export-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtCurrency(n) {
  return `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPhone(num) { if (!num) return ''; const d = String(num).replace(/\D/g,''); if (d.length === 11 && d[0]==='1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`; if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`; return num; }

const CX_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/cxone-proxy'
  : '/.netlify/functions/cxone-proxy';

const ARCHIVE_PROXY = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/archive-recordings'
  : '/.netlify/functions/archive-recordings';

// Email history moved off the cp-dotdigital proxy on 2026-08-24. The proxy version asked
// Dotdigital about all 89 sent campaigns on every lookup and most of those answer 404, which
// locked the shared API user out and took newsletter sending down with it. The CPx function
// caches every answer in Neon and caps how many it will ask at once.
const DD_ACTIVITY_FN = (window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions') + '/dotdigital-contact-activity';

/* ── Inline Recording Player ── */
function MiniPlayer({ url, streamAction }) {
  const [state, setState] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef(null);
  const blobRef = useRef(null);

  const loadAudio = async () => {
    if (state === 'loading') return;
    setState('loading');
    try {
      const proxy = streamAction === 'r2' ? ARCHIVE_PROXY : CX_PROXY;
      const body = streamAction === 'r2'
        ? { action: 'stream', params: { r2Key: url } }
        : { action: 'recording-stream', params: { url } };
      const res = await fetch(proxy, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      blobRef.current = blobUrl;
      const audio = new Audio(blobUrl);
      audioRef.current = audio;
      audio.addEventListener('loadedmetadata', () => { setDuration(audio.duration); setState('ready'); audio.play(); setState('playing'); });
      audio.addEventListener('timeupdate', () => { setCurrentTime(audio.currentTime); setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0); });
      audio.addEventListener('ended', () => setState('ready'));
      audio.addEventListener('error', () => setState('error'));
      audio.load();
    } catch { setState('error'); }
  };

  const togglePlay = () => {
    if (!audioRef.current) return loadAudio();
    if (state === 'playing') { audioRef.current.pause(); setState('ready'); }
    else { audioRef.current.play(); setState('playing'); }
  };

  const seek = (e) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audioRef.current.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * duration;
  };

  useEffect(() => () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
  }, []);

  const ft = (s) => { if (!s || !isFinite(s)) return '0:00'; return `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`; };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 160 }}>
      <button onClick={togglePlay} disabled={state === 'loading'} style={{
        background: state === 'playing' ? 'var(--orange)' : 'var(--navy)', border: 'none', borderRadius: '50%',
        width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: state === 'loading' ? 'wait' : 'pointer', flexShrink: 0, opacity: state === 'loading' ? 0.5 : 1,
      }}>
        {state === 'loading' ? <RefreshCw size={10} color="#fff" style={{ animation: 'spin 1s linear infinite' }} />
          : state === 'playing' ? <Pause size={10} color="#fff" />
          : <Play size={10} color="#fff" style={{ marginLeft: 1 }} />}
      </button>
      {(state === 'ready' || state === 'playing') && duration > 0 ? (
        <>
          <div onClick={seek} style={{ flex: 1, height: 5, background: 'var(--gray-200)', borderRadius: 3, cursor: 'pointer', minWidth: 60 }}>
            <div style={{ width: `${progress}%`, height: '100%', background: state === 'playing' ? 'var(--orange)' : 'var(--navy)', borderRadius: 3, transition: 'width 0.1s linear' }} />
          </div>
          <span style={{ fontSize: 9, color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{ft(currentTime)}/{ft(duration)}</span>
        </>
      ) : state === 'error' ? <span style={{ fontSize: 10, color: 'var(--red)' }}>Error</span>
        : state === 'idle' ? <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>Play</span>
        : null}
    </div>
  );
}

/* ── Contact History Section ── */
// Social-media comments attached to this customer from the Social Media Builder
// comments panel (📌 Attach comment). Auto-loads and renders ONLY when notes
// exist — an amber flag so CS can't miss it when the customer calls in.
function SocialNotes({ customerId }) {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    let alive = true;
    setNotes([]);
    if (!customerId) return undefined;
    neonQuery('social_notes_for_customer', { customerId })
      .then(rows => { if (alive) setNotes(Array.isArray(rows) ? rows : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [customerId]);
  if (!notes.length) return null;
  const fmtDt = (iso) => { try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
  const removeNote = async (id) => {
    if (!window.confirm('Remove this social media note from the customer record?')) return;
    try { await neonQuery('social_note_delete', { id }); setNotes(p => p.filter(n => n.id !== id)); } catch (e) { /* keep row on failure */ }
  };
  return (
    <div className="card" style={{ marginBottom: 12, border: '1px solid #f0c36d', background: '#fffaf0' }}>
      <div className="card-header" style={{ background: '#fff3d6', color: '#7a5b00', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>⚠ Social Media Notes ({notes.length})</span>
        <span style={{ fontSize: 10.5, fontWeight: 400, color: '#9a7b20' }}>attached from Facebook / Instagram comments — read before handling this customer</span>
      </div>
      <div style={{ padding: '10px 16px' }}>
        {notes.map(n => (
          <div key={n.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3e6c4', fontSize: 12 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 3 }}>
              <span style={{ fontSize: 9.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4, color: '#fff', background: n.platform === 'instagram' ? '#E1306C' : '#1877F2', borderRadius: 3, padding: '1px 6px' }}>{n.platform === 'instagram' ? 'Instagram' : 'Facebook'}</span>
              {n.commenter_name && <strong style={{ color: 'var(--navy)' }}>{n.commenter_name}</strong>}
              <span style={{ color: 'var(--gray-500)', fontSize: 11 }}>{fmtDt(n.created_at)}{n.attached_by ? ` · attached by ${n.attached_by}` : ''}</span>
              {n.permalink && <a href={n.permalink} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--orange)', fontWeight: 600 }}>View post ↗</a>}
              <button onClick={() => removeNote(n.id)} title="Remove this note" style={{ background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: 11, padding: 0, marginLeft: 'auto' }}>✕</button>
            </div>
            <div style={{ color: 'var(--navy)', lineHeight: 1.5 }}>“{n.comment_text}”</div>
            {n.post_caption && <div style={{ color: 'var(--gray-500)', fontSize: 11, marginTop: 2 }}>On post: {String(n.post_caption).slice(0, 120)}{String(n.post_caption).length > 120 ? '…' : ''}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContactHistory({ phone, email }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('calls');
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailLoaded, setEmailLoaded] = useState(false);
  const [emailShow, setEmailShow] = useState(5);

  const phoneLast10 = (phone || '').replace(/\D/g, '').slice(-10);

  const loadCalls = useCallback(async () => {
    if (!phoneLast10) return;
    setLoading(true);
    try {
      // Search archived recordings by phone number
      const res = await fetch(ARCHIVE_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'search', params: { query: phoneLast10, pageSize: 100 } }),
      });
      const data = await res.json();
      setCalls(data.recordings || []);
    } catch {}
    setLoading(false);
    setLoaded(true);
  }, [phoneLast10]);

  const loadEmails = useCallback(async () => {
    if (!email) return;
    setEmailLoading(true);
    try {
      const token = getAccessToken();
      const res = await fetch(DD_ACTIVITY_FN + '?email=' + encodeURIComponent(email), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();
      setEmails(data.activity || []);
    } catch {}
    setEmailLoading(false);
    setEmailLoaded(true);
  }, [email]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) loadCalls();
  };

  const formatDuration = (s) => { if (!s) return '0s'; const m = Math.floor(s/60); const r = s%60; return m > 0 ? (r > 0 ? `${m}m ${r}s` : `${m}m`) : `${s}s`; };
  const formatDt = (iso) => { if (!iso) return ''; return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }); };


  const tabs = [
    { id: 'calls', label: 'Phone Calls', icon: Phone, count: calls.length },
    { id: 'sms', label: 'SMS', icon: MessageSquare, count: 0 },
    { id: 'email', label: 'Email', icon: Mail, count: emails.length },
  ];

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div
        className="card-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={handleToggle}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          Customer Contact History
          {(calls.length + emails.length) > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--orange)', color: '#fff', borderRadius: 10, padding: '1px 7px' }}>{calls.length + emails.length}</span>}
        </span>
      </div>

      {open && (
        <div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--gray-200)', padding: '0 16px' }}>
            {tabs.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'email' && !emailLoaded) loadEmails(); }} style={{
                  padding: '8px 16px', fontSize: 11, fontWeight: active ? 700 : 500,
                  color: active ? 'var(--orange)' : 'var(--gray-500)',
                  background: 'none', border: 'none', borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
                  marginBottom: -1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <Icon size={12} /> {t.label}
                  {t.count > 0 && <span style={{ fontSize: 9, fontWeight: 700, color: active ? 'var(--orange)' : 'var(--gray-500)' }}>({t.count})</span>}
                </button>
              );
            })}
          </div>

          {/* Call History */}
          {tab === 'calls' && (
            <div style={{ padding: 0 }}>
              {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading call history...</div>}
              {!loading && calls.length === 0 && (
                <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>
                  {!phoneLast10 ? 'No phone number on file.' : 'No call records found for this customer.'}
                </div>
              )}
              {!loading && calls.length > 0 && (
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Direction</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Agent</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Duration</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Recording</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calls.map((c, i) => (
                      <tr key={c.contact_id} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                        <td style={{ padding: '6px 12px' }}>{formatDt(c.contact_date)}</td>
                        <td style={{ padding: '6px 8px' }}>
                          <span style={{ fontSize: 10, fontWeight: 600, color: c.is_outbound ? '#2563eb' : '#1a8a4a' }}>
                            {c.is_outbound ? '↗ Outbound' : '↙ Inbound'}
                          </span>
                        </td>
                        <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--navy)' }}>{c.agent_name || '—'}</td>
                        <td style={{ padding: '6px 8px' }}>{formatDuration(c.duration_seconds)}</td>
                        <td style={{ padding: '6px 8px', minWidth: 170 }}>
                          {c.r2_key ? <MiniPlayer url={c.r2_key} streamAction="r2" /> : <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>N/A</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* SMS - placeholder */}
          {tab === 'sms' && (
            <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>
              SMS history coming soon.
            </div>
          )}

          {/* Email — Dotdigital campaign sends */}
          {tab === 'email' && (
            <div style={{ padding: 0 }}>
              {emailLoading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading email history...</div>}
              {!emailLoading && emails.length === 0 && (
                <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>
                  {!email ? 'No email address on file.' : 'No newsletter sends found for this customer.'}
                </div>
              )}
              {!emailLoading && emails.length > 0 && (
                <>
                  <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                        <th style={{ textAlign: 'left', padding: '6px 12px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Date Sent</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Campaign</th>
                        <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Subject</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Opened</th>
                        <th style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600, color: 'var(--gray-500)', fontSize: 10, textTransform: 'uppercase' }}>Clicked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emails.slice(0, emailShow).map((e, i) => {
                        const sent = e.dateSent;
                        const opened = e.numOpens > 0 && e.dateFirstOpened ? e.dateFirstOpened : null;
                        const clicked = e.numClicks > 0;
                        return (
                          <tr key={e.campaignId + '-' + i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                            <td style={{ padding: '6px 12px' }}>{sent ? formatDt(sent) : '—'}</td>
                            <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--navy)' }}>{e.campaignName || '—'}</td>
                            <td style={{ padding: '6px 8px' }}>{e.subject || '—'}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              {opened
                                ? <span style={{ fontSize: 10, fontWeight: 600, color: '#1a8a4a' }}>{formatDt(opened)}</span>
                                : <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>—</span>}
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              {clicked
                                ? <span style={{ fontSize: 10, fontWeight: 600, color: '#2563eb' }}>Yes</span>
                                : <span style={{ fontSize: 10, color: 'var(--gray-400)' }}>—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {emailShow < emails.length && (
                    <div style={{ padding: '8px 12px', textAlign: 'center', borderTop: '1px solid var(--gray-100)' }}>
                      <button onClick={() => setEmailShow(s => s + 5)} style={{ fontSize: 11, fontWeight: 600, color: 'var(--orange)', background: 'none', border: 'none', cursor: 'pointer' }}>
                        Show more ({emails.length - emailShow} remaining)
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Address Book (from BigCommerce) ── */
function AddressBook({ bcCustomerId }) {
  const [open, setOpen] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [newAddr, setNewAddr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editAddr, setEditAddr] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const emptyAddr = { first_name: '', last_name: '', company: '', address1: '', address2: '', city: '', state_or_province: '', postal_code: '', country_code: 'US', phone: '', address_type: 'residential' };

  const load = useCallback(async () => {
    if (!bcCustomerId) return;
    setLoading(true);
    try {
      const res = await bcProxy({ path: `/v3/customers?id:in=${bcCustomerId}&include=addresses`, method: 'GET' });
      const data = await res.json();
      setAddresses(data?.data?.[0]?.addresses || []);
    } catch {}
    setLoading(false);
    setLoaded(true);
  }, [bcCustomerId]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) load();
  };

  const deleteAddress = async (addr) => {
    if (!addr.id) return;
    const label = [addr.first_name, addr.last_name].filter(Boolean).join(' ') + ' — ' + (addr.address1 || '');
    if (!window.confirm(`Delete this address?\n\n${label}`)) return;
    if (!window.confirm('Are you sure? This will permanently remove the address from BigCommerce.')) return;
    try {
      const res = await bcProxy({ path: `/v3/customers/addresses?id:in=${addr.id}`, method: 'DELETE' });
      if (res.ok) {
        setAddresses(prev => prev.filter(a => a.id !== addr.id));
      } else {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.title || 'Failed to delete');
      }
    } catch (err) {
      alert('Error deleting address: ' + err.message);
    }
  };

  const saveNewAddress = async () => {
    if (!newAddr.first_name || !newAddr.address1 || !newAddr.city || !newAddr.state_or_province || !newAddr.postal_code) {
      alert('Please fill in name, address, city, state, and zip.');
      return;
    }
    setSaving(true);
    try {
      const res = await bcProxy({
        path: '/v3/customers/addresses',
        method: 'POST',
        body: [{ ...newAddr, customer_id: Number(bcCustomerId) }],
      });
      const data = await res.json();
      console.log('[AddressBook] BC response:', data);
      const created = data?.data?.[0];
      if (!created?.id) throw new Error(data?.title || data?.errors ? JSON.stringify(data.errors) : 'Failed to create address');
      setAddresses(prev => [...prev, created]);
      setNewAddr(null);
    } catch (e) {
      alert('Error creating address: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setEditAddr({
      first_name: a.first_name || '', last_name: a.last_name || '', company: a.company || '',
      address1: a.address1 || a.street_1 || '', address2: a.address2 || a.street_2 || '',
      city: a.city || '', state_or_province: a.state_or_province || a.state || '',
      postal_code: a.postal_code || a.zip || '', country_code: a.country_code || 'US',
      phone: a.phone || '', address_type: a.address_type || 'residential',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditAddr(null); };

  const saveEdit = async () => {
    if (!editAddr.first_name || !editAddr.address1 || !editAddr.city || !editAddr.state_or_province || !editAddr.postal_code) {
      alert('Please fill in name, address, city, state, and zip.');
      return;
    }
    setEditSaving(true);
    try {
      const res = await bcProxy({
        path: '/v3/customers/addresses',
        method: 'PUT',
        body: [{ id: editingId, customer_id: Number(bcCustomerId), ...editAddr }],
      });
      const data = await res.json();
      const updated = data?.data?.[0];
      if (!updated?.id) throw new Error(data?.title || (data?.errors ? JSON.stringify(data.errors) : 'Failed to update address'));
      setAddresses(prev => prev.map(a => a.id === editingId ? updated : a));
      cancelEdit();
    } catch (e) {
      alert('Error updating address: ' + e.message);
    } finally {
      setEditSaving(false);
    }
  };

  const [defaultSaving, setDefaultSaving] = useState(false);

  const setAsDefault = async (addr) => {
    if (!addr.id || defaultSaving) return;
    if (!window.confirm(`Set "${[addr.first_name, addr.last_name].filter(Boolean).join(' ')} — ${addr.address1}" as the default address?`)) return;
    setDefaultSaving(true);
    try {
      // BC doesn't have a "set default" API — we delete and recreate as the
      // first address for this customer, which makes it the new default.
      // Step 1: Delete the old address
      const delRes = await bcProxy({ path: `/v3/customers/addresses?id:in=${addr.id}`, method: 'DELETE' });
      if (!delRes.ok) throw new Error('Failed to remove old address');

      // Step 2: Recreate it — newly created address becomes default
      const newAddr = {
        customer_id: Number(bcCustomerId),
        first_name: addr.first_name || '', last_name: addr.last_name || '',
        company: addr.company || '',
        address1: addr.address1 || addr.street_1 || '',
        address2: addr.address2 || addr.street_2 || '',
        city: addr.city || '',
        state_or_province: addr.state_or_province || addr.state || '',
        postal_code: addr.postal_code || addr.zip || '',
        country_code: addr.country_code || 'US',
        phone: addr.phone || '',
        address_type: addr.address_type || 'residential',
      };
      const createRes = await bcProxy({ path: '/v3/customers/addresses', method: 'POST', body: [newAddr] });
      const createData = await createRes.json();
      const created = createData?.data?.[0];
      if (!created?.id) throw new Error('Failed to recreate address');

      // Reload full address list from BC to get correct order
      await load();
    } catch (e) {
      alert('Error setting default: ' + e.message);
      // Reload to restore correct state
      await load();
    } finally {
      setDefaultSaving(false);
    }
  };

  if (!bcCustomerId) return null;

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={handleToggle}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <MapPin size={14} /> Address Book
          {loaded && addresses.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--navy)', color: '#fff', borderRadius: 10, padding: '1px 7px' }}>{addresses.length}</span>}
        </span>
      </div>
      {open && (
        <div style={{ padding: '12px 16px' }}>
          {loading && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading addresses...</div>}
          {!loading && addresses.length === 0 && !newAddr && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No addresses on file.</div>}
          {!loading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 12 }}>
              {addresses.map((a, i) => (
                <div key={a.id || i} style={{
                  padding: '10px 14px', border: editingId === a.id ? '1px solid var(--orange)' : '1px solid var(--gray-200)', borderRadius: 6,
                  background: i === 0 ? '#f0f7ff' : 'var(--gray-50)', fontSize: 12,
                }}>
                  {editingId === a.id && editAddr ? (
                    /* ── Inline Edit Form ── */
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--navy)', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Edit Address
                        <span style={{ display: 'flex', gap: 4 }}>
                          <span onClick={saveEdit} title="Save" style={{ color: 'var(--orange)', cursor: editSaving ? 'wait' : 'pointer', display: 'flex' }}><Check size={14} /></span>
                          <span onClick={cancelEdit} title="Cancel" style={{ color: 'var(--gray-400)', cursor: 'pointer', display: 'flex' }}><XIcon size={14} /></span>
                        </span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 8px' }}>
                        <input className="form-input" placeholder="First Name *" value={editAddr.first_name} onChange={e => setEditAddr(p => ({ ...p, first_name: e.target.value }))} style={{ fontSize: 11 }} />
                        <input className="form-input" placeholder="Last Name" value={editAddr.last_name} onChange={e => setEditAddr(p => ({ ...p, last_name: e.target.value }))} style={{ fontSize: 11 }} />
                        <input className="form-input" placeholder="Company" value={editAddr.company} onChange={e => setEditAddr(p => ({ ...p, company: e.target.value }))} style={{ fontSize: 11, gridColumn: '1 / -1' }} />
                        <input className="form-input" placeholder="Address Line 1 *" value={editAddr.address1} onChange={e => setEditAddr(p => ({ ...p, address1: e.target.value }))} style={{ fontSize: 11, gridColumn: '1 / -1' }} />
                        <input className="form-input" placeholder="Address Line 2" value={editAddr.address2} onChange={e => setEditAddr(p => ({ ...p, address2: e.target.value }))} style={{ fontSize: 11, gridColumn: '1 / -1' }} />
                        <input className="form-input" placeholder="City *" value={editAddr.city} onChange={e => setEditAddr(p => ({ ...p, city: e.target.value }))} style={{ fontSize: 11 }} />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <select className="form-input" value={editAddr.state_or_province} onChange={e => setEditAddr(p => ({ ...p, state_or_province: e.target.value }))} style={{ fontSize: 11, width: '50%' }}>
                            <option value="">State *</option>
                            {US_STATES.map(([abbr, name]) => <option key={abbr} value={name}>{abbr}</option>)}
                          </select>
                          <input className="form-input" placeholder="ZIP *" value={editAddr.postal_code} onChange={e => setEditAddr(p => ({ ...p, postal_code: e.target.value }))} style={{ fontSize: 11, width: '50%' }} />
                        </div>
                        <input className="form-input" placeholder="Phone" value={editAddr.phone} onChange={e => setEditAddr(p => ({ ...p, phone: e.target.value }))} style={{ fontSize: 11 }} />
                        <select className="form-input" value={editAddr.address_type} onChange={e => setEditAddr(p => ({ ...p, address_type: e.target.value }))} style={{ fontSize: 11 }}>
                          <option value="residential">Residential</option>
                          <option value="commercial">Commercial</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    /* ── Display Mode ── */
                    <>
                      <div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>{toTitleCase(a.first_name)} {toTitleCase(a.last_name)}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {i === 0 && <span style={{ fontSize: 9, fontWeight: 600, background: 'var(--navy)', color: '#fff', padding: '1px 6px', borderRadius: 4 }}>DEFAULT</span>}
                          <span onClick={() => startEdit(a)} title="Edit address"
                            style={{ color: 'var(--gray-400)', cursor: 'pointer', display: 'flex' }}
                            onMouseEnter={e => e.currentTarget.style.color = 'var(--orange)'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--gray-400)'}><Edit2 size={12} /></span>
                          <span onClick={() => deleteAddress(a)} title="Delete address"
                            style={{ color: 'var(--gray-400)', cursor: 'pointer', display: 'flex' }}
                            onMouseEnter={e => e.currentTarget.style.color = '#c0392b'}
                            onMouseLeave={e => e.currentTarget.style.color = 'var(--gray-400)'}><Trash2 size={12} /></span>
                        </span>
                      </div>
                      {a.company && <div style={{ color: 'var(--gray-700)', marginBottom: 2 }}>{toTitleCase(a.company)}</div>}
                      <div style={{ color: 'var(--gray-700)' }}>{toTitleCase(a.address1 || a.street_1)}</div>
                      {(a.address2 || a.street_2) && <div style={{ color: 'var(--gray-700)' }}>{toTitleCase(a.address2 || a.street_2)}</div>}
                      <div style={{ color: 'var(--gray-700)' }}>
                        {toTitleCase(a.city)}, {stateAbbr(a.state_or_province || a.state || '')} {a.postal_code || a.zip}
                      </div>
                      {a.phone && <div style={{ color: 'var(--gray-500)', marginTop: 4, fontSize: 11 }}>{formatPhone(a.phone)}</div>}
                      {a.address_type && <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2, textTransform: 'capitalize' }}>{a.address_type}</div>}
                      {i !== 0 && (
                        <div style={{ marginTop: 6 }}>
                          <span onClick={() => setAsDefault(a)} style={{ fontSize: 10, color: 'var(--orange)', cursor: defaultSaving ? 'wait' : 'pointer', fontWeight: 600, opacity: defaultSaving ? 0.5 : 1 }}
                            onMouseEnter={e => { if (!defaultSaving) e.currentTarget.style.textDecoration = 'underline'; }}
                            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>{defaultSaving ? 'Updating...' : 'Set as Default'}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              {/* + New Address card */}
              {!newAddr && (
                <div onClick={() => setNewAddr({ ...emptyAddr })}
                  style={{
                    padding: '10px 14px', border: '2px dashed var(--gray-300)', borderRadius: 6,
                    background: 'var(--gray-50)', fontSize: 12, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 80,
                    color: 'var(--gray-500)', fontWeight: 600, transition: 'border-color .15s, color .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--orange)'; e.currentTarget.style.color = 'var(--orange)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--gray-300)'; e.currentTarget.style.color = 'var(--gray-500)'; }}>
                  + New Address
                </div>
              )}
            </div>
          )}
          {/* New Address Form */}
          {newAddr && (
            <div style={{ marginTop: 12, padding: 16, border: '1px solid var(--gray-200)', borderRadius: 8, background: '#fafafa' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--navy)', marginBottom: 12 }}>New Address</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px', fontSize: 12 }}>
                <input className="form-input" placeholder="First Name *" value={newAddr.first_name} onChange={e => setNewAddr(a => ({ ...a, first_name: e.target.value }))} style={{ fontSize: 12 }} />
                <input className="form-input" placeholder="Last Name" value={newAddr.last_name} onChange={e => setNewAddr(a => ({ ...a, last_name: e.target.value }))} style={{ fontSize: 12 }} />
                <input className="form-input" placeholder="Company" value={newAddr.company} onChange={e => setNewAddr(a => ({ ...a, company: e.target.value }))} style={{ fontSize: 12, gridColumn: '1 / -1' }} />
                <input className="form-input" placeholder="Address Line 1 *" value={newAddr.address1} onChange={e => setNewAddr(a => ({ ...a, address1: e.target.value }))} style={{ fontSize: 12, gridColumn: '1 / -1' }} />
                <input className="form-input" placeholder="Address Line 2" value={newAddr.address2} onChange={e => setNewAddr(a => ({ ...a, address2: e.target.value }))} style={{ fontSize: 12, gridColumn: '1 / -1' }} />
                <input className="form-input" placeholder="City *" value={newAddr.city} onChange={e => setNewAddr(a => ({ ...a, city: e.target.value }))} style={{ fontSize: 12 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <select className="form-input" value={newAddr.state_or_province} onChange={e => setNewAddr(a => ({ ...a, state_or_province: e.target.value }))} style={{ fontSize: 12, width: '50%' }}>
                      <option value="">State *</option>
                      {US_STATES.map(([abbr, name]) => <option key={abbr} value={name}>{abbr}</option>)}
                    </select>
                  <input className="form-input" placeholder="ZIP *" value={newAddr.postal_code} onChange={e => setNewAddr(a => ({ ...a, postal_code: e.target.value }))} style={{ fontSize: 12, width: '50%' }} />
                </div>
                <input className="form-input" placeholder="Phone" value={newAddr.phone} onChange={e => setNewAddr(a => ({ ...a, phone: e.target.value }))} style={{ fontSize: 12 }} />
                <select className="form-input" value={newAddr.address_type} onChange={e => setNewAddr(a => ({ ...a, address_type: e.target.value }))} style={{ fontSize: 12 }}>
                  <option value="residential">Residential</option>
                  <option value="commercial">Commercial</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn" onClick={() => setNewAddr(null)} style={{ fontSize: 12, padding: '6px 14px' }}>Cancel</button>
                <button className="btn btn-primary" onClick={saveNewAddress} disabled={saving} style={{ fontSize: 12, padding: '6px 14px' }}>
                  {saving ? 'Saving...' : 'Save to BigCommerce'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Payment Profiles (Authorize.net CIM) ── */
function PaymentProfile({ customerId, customerProfileId }) {
  const [open, setOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const proxy = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions/cim-proxy'
        : '/.netlify/functions/cim-proxy';
      const res = await fetch(proxy, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        // Backend returned an error — show friendly message instead of raw status
        const msg = data?.error || data?.message || '';
        if (res.status === 500) {
          // Could be auth failure, Authorize.net down, or customer not found in backend
          console.warn('[PaymentProfile] Backend 500 for customerId:', customerId, msg);
          setProfiles([]);
        } else {
          throw new Error(msg || `API error ${res.status}`);
        }
      } else {
        // Check for backend "failed" message (200 with error body)
        if (typeof data === 'string' || data?.message?.toLowerCase?.()?.includes('fail')) {
          setProfiles([]);
        } else {
          setProfiles(data.profile?.paymentProfiles || data.paymentProfiles || []);
        }
      }
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
    setLoaded(true);
  }, [customerId]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded) load();
  };

  const [defaultSaving, setDefaultSaving] = useState(false);

  const setDefaultProfile = async (profileId) => {
    if (defaultSaving) return;
    setDefaultSaving(true);
    try {
      const proxy = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions/cim-proxy'
        : '/.netlify/functions/cim-proxy';
      const res = await fetch(proxy, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'setDefault', customerId, paymentProfileId: profileId }),
      });
      if (!res.ok) throw new Error('Failed to set default');
      // Reload profiles to reflect new default
      await load();
    } catch (e) {
      alert('Error setting default: ' + e.message);
    } finally {
      setDefaultSaving(false);
    }
  };

  const cardLabel = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('visa')) return 'Visa';
    if (t.includes('master')) return 'Mastercard';
    if (t.includes('amex') || t.includes('american')) return 'Amex';
    if (t.includes('discover')) return 'Discover';
    return type || 'Card';
  };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={handleToggle}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <CreditCard size={14} /> Payment Profiles
          {customerProfileId
            ? <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 4, background: '#dcfce7', color: '#166534' }}>On File</span>
            : <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 4, background: '#fee2e2', color: '#991b1b' }}>None</span>}
          {loaded && profiles.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--navy)', color: '#fff', borderRadius: 10, padding: '1px 7px' }}>{profiles.length}</span>}
        </span>
      </div>
      {open && (
        <div style={{ padding: '12px 16px' }}>
          {loading && <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Loading payment profiles...</div>}
          {error && <div style={{ fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
          {!loading && !error && profiles.length === 0 && loaded && (
            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>No payment profiles on file.</div>
          )}
          {!loading && profiles.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
              {profiles.map((p, i) => {
                const card = p.payment?.item;
                const bill = p.billTo;
                return (
                  <div key={p.customerPaymentProfileId || i} style={{
                    padding: '10px 14px', border: '1px solid var(--gray-200)', borderRadius: 6,
                    background: p.defaultPaymentProfile ? '#f0f7ff' : 'var(--gray-50)', fontSize: 12,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13 }}>
                        {card ? cardLabel(card.cardType) : 'Card'}
                      </span>
                      {p.defaultPaymentProfile && (
                        <span style={{ fontSize: 9, fontWeight: 600, background: 'var(--navy)', color: '#fff', padding: '1px 6px', borderRadius: 4 }}>DEFAULT</span>
                      )}
                    </div>
                    {card && (
                      <div style={{ marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, letterSpacing: 1, color: 'var(--gray-900)' }}>{card.cardNumber}</div>
                        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                          Exp: {card.expirationDate === 'XXXX' ? 'On file' : card.expirationDate}
                        </div>
                      </div>
                    )}
                    {bill && (
                      <div style={{ fontSize: 11, color: 'var(--gray-700)', borderTop: '1px solid var(--gray-200)', paddingTop: 6 }}>
                        <div style={{ fontWeight: 600 }}>{[bill.firstName, bill.lastName].filter(Boolean).join(' ')}</div>
                        {bill.address && <div>{bill.address}</div>}
                        <div>{[bill.city, bill.state, bill.zip].filter(Boolean).join(', ')}</div>
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Profile ID: {p.customerPaymentProfileId}</span>
                      {!p.defaultPaymentProfile && (
                        <span onClick={() => setDefaultProfile(p.customerPaymentProfileId)}
                          style={{ color: 'var(--orange)', cursor: defaultSaving ? 'wait' : 'pointer', fontWeight: 600, opacity: defaultSaving ? 0.5 : 1 }}
                          onMouseEnter={e => { if (!defaultSaving) e.currentTarget.style.textDecoration = 'underline'; }}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                          {defaultSaving ? 'Updating...' : 'Set as Default'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Linked Accounts (lib/linked-accounts.js): other customer accounts that belong to this person
// or household. Joseph Tufano, 2026-09-29, was a paying VIP under one email and got the lapsed-VIP
// win-back at the other; CS could not see that from this screen. Totals here are the PERSON'S,
// across every linked account, beside the single-account numbers in the header.
function LinkedAccountsCard({ customerId, onOpen, navigateTo, onChanged }) {
  const [group, setGroup] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    setGroup(undefined);
    neonQuery('identity_group_for_customer', { contactId: customerId })
      .then(r => setGroup((r && r.group) || null)).catch(() => setGroup(null));
  }, [customerId]);
  useEffect(() => { load(); }, [load]);
  if (!group) return null;
  const user = getCurrentUser() || {};
  const verdict = async (v) => {
    setBusy(true);
    try {
      await neonQuery('identity_group_review', { groupId: group.id, verdict: v, adminId: user.id, adminName: user.staffName || user.name || null });
      load(); if (onChanged) onChanged();
    } finally { setBusy(false); }
  };
  const ms = group.members;
  const others = ms.filter(m => String(m.contactId) !== String(customerId));
  const orders = ms.reduce((a, m) => a + (m.orders || 0), 0);
  const spend = ms.reduce((a, m) => a + (m.spend || 0), 0);
  const last = ms.map(m => m.lastOrder).filter(Boolean).sort().pop();
  const household = group.kind === 'household';
  const signalLabel = { phone: 'phone', addr: 'billing address', email: 'email' };
  const vipTag = (m) => m.vip === 'active'
    ? <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--orange)', color: '#fff', padding: '1px 6px', borderRadius: 8 }}>VIP</span>
    : m.vip === 'lapsed'
      ? <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--gray-200)', color: '#555', padding: '1px 6px', borderRadius: 8 }}>Lapsed VIP{m.vipExpires ? ` ${fmtDate(m.vipExpires)}` : ''}</span>
      : null;
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span>🔗 {household ? 'Same household' : 'Same person'}: {others.length} other account{others.length === 1 ? '' : 's'}</span>
        <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--gray-500)' }}>
          {group.reviewed
            ? `Confirmed${group.reviewedBy ? ' by ' + group.reviewedBy : ''}${group.reviewedOn ? ' ' + group.reviewedOn : ''}`
            : `Found by the nightly scan${group.signals.length ? ' (shared ' + group.signals.map(x => signalLabel[x] || x).join(', ') + ')' : ''}, not reviewed yet`}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {!group.reviewed && <button className="btn btn-navy" disabled={busy} style={{ fontSize: 11 }} onClick={() => verdict(household ? 'household' : 'person')}>Confirm</button>}
          {!group.reviewed && <button className="btn" disabled={busy} style={{ fontSize: 11 }} onClick={() => verdict(household ? 'person' : 'household')}>{household ? 'Same person' : 'Household'}</button>}
          <button className="btn" disabled={busy} style={{ fontSize: 11, color: '#b3261e' }} onClick={() => verdict('no')}>Not related</button>
          {navigateTo && <button className="btn" style={{ fontSize: 11 }} onClick={() => navigateTo('linked-accounts', { q: String(customerId) })}>Open in Linked Accounts</button>}
        </span>
      </div>
      <div style={{ padding: '10px 16px', fontSize: 12, display: 'grid', gap: 6 }}>
        {others.map(m => (
          <div key={m.contactId} onClick={() => onOpen(m)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 10px', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: 'var(--navy)' }}>{toTitleCase(m.name)}</span>
            {vipTag(m)}
            <span style={{ color: 'var(--orange)' }}>{m.email}</span>
            {m.phone && <span style={{ color: 'var(--gray-500)' }}>{m.phone}</span>}
            <span style={{ marginLeft: 'auto', color: 'var(--gray-500)' }}>{m.orders} order{m.orders === 1 ? '' : 's'} · {fmtCurrency(m.spend)}{m.lastOrder ? ` · last ${fmtDate(m.lastOrder)}` : ''} · #{m.contactId}</span>
          </div>
        ))}
        {!household && (
          <div style={{ color: 'var(--gray-500)', marginTop: 2 }}>
            <b style={{ color: 'var(--navy)' }}>Combined:</b> {orders.toLocaleString()} orders · {fmtCurrency(spend)} lifetime{last ? ` · last order ${fmtDate(last)}` : ''}
            {ms.some(m => m.vip === 'active') && ' · VIP through the linked account'}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerSearch({ navigateTo, initialQuery }) {
  const [q, setQ] = useState('');
  const [customers, setCustomers] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storeCredit, setStoreCredit] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [linkMap, setLinkMap] = useState({});

  const user = getCurrentUser();
  const userRoles = Array.isArray(user?.roles) ? user.roles : (user?.roles || '').split(',').map(r => r.trim());
  const canLoginAsCustomer = userRoles.includes('Admin') || userRoles.includes('Manager') || userRoles.includes('Customer Service');

  // ── Jimmy chat ──────────────────────────────────────────────────────────────
  const [chatOpen, setChatOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [displayMessages, setDisplayMessages] = useState([]);
  const [apiMessages, setApiMessages] = useState([]);
  const chatEndRef = useRef(null);

  const CHAT_BASE = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions'
    : '/.netlify/functions';

  // Reset chat when customer changes
  useEffect(() => {
    setDisplayMessages([]);
    setApiMessages([]);
    setChatInput('');
  }, [selectedCustomer?.id]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages, chatLoading]);

  const ANALYTIC_TOOLS = ['top_n', 'distribution', 'segment_stats', 'group_by', 'cohort_compare', 'trend', 'search_customers', 'list_notable_customers'];

  const executeTool = async (name, input) => {
    try {
      if (ANALYTIC_TOOLS.includes(name)) {
        const rows = await neonQuery('jimmy_analytics', { tool: name, args: input });
        return {
          result: JSON.stringify({ rowCount: rows.length, rows }),
          exportRows: rows?.length ? rows : null,
        };
      }
      if (name === 'run_sql') {
        const result = await neonRawSelect(input.sql);
        return { result: JSON.stringify({ rowCount: result.rowCount, rows: result.rows, sql: result.sql }), exportRows: result.rows?.length ? result.rows : null };
      }
      return { result: JSON.stringify({ error: 'Unknown tool' }), exportRows: null };
    } catch (e) {
      return { result: JSON.stringify({ error: e.message }), exportRows: null };
    }
  };

  const buildSystemContext = () => {
    if (!selectedCustomer) return `## Context
You are on the Customer Search page with no specific customer selected. Answer general questions about the customer base using run_sql.

## Database Schema (Neon — use these exact column names)
customers (contactid TEXT PK, email, firstname, lastname, phone)
vip_memberships (id, customer_id INTEGER, joined_date, expiration_date, active BOOLEAN, auto_renew BOOLEAN, has_payment_profile BOOLEAN)
  -- JOIN: vm.customer_id::text = c.contactid (type cast required)
addresses (address_id PK, first_name, last_name, address TEXT, address2, city, state, zip, phone)
  -- JOIN via orders: orders.bill_to_id = addresses.address_id OR orders.ship_to_id = addresses.address_id
orders (orderid TEXT PK, bc_order_id TEXT, ocustomerid TEXT, bill_to_id, ship_to_id, odate TEXT, shipdate TEXT, oshipmethodtype TEXT, vatnumber TEXT, orderamount TEXT, oshipcost TEXT, coupon TEXT, coupondiscount TEXT)
  -- JOIN: o.ocustomerid = c.contactid
  -- NOTE: bc_order_id is THE order number staff use. Always select and show it (AS order_number); never output orderid, which is an internal key for joins only.
  -- NOTE: orderamount, oshipcost etc. are TEXT — cast with ::numeric for math
oitems (orderitemid, orderid, itemname TEXT, partnumber TEXT, numitems INTEGER, unitprice NUMERIC)
order_item_options (id, order_item_id, feature TEXT, value TEXT)

Use run_sql for any question about customers, orders, geography, or behavior.`;
    const name = [selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ') || 'Unknown';
    const orderSummary = orders && orders.length > 0
      ? `They have ${orders.length} orders in the database. Most recent: ${fmtDate(orders[0]?.order_date)}, $${Number(orders[0]?.order_amount || 0).toFixed(2)}.`
      : 'No orders found in the local database (may be in production only).';
    return `## Current Customer Context
You are currently viewing a specific customer in the Customer Search page. Focus your answers on this customer unless asked otherwise.

**Customer:** ${name}
**Email:** ${selectedCustomer.email || '—'}
**Customer ID (Neon DB):** ${selectedCustomer.id}
**Total Orders:** ${Number(selectedCustomer.order_count || 0).toLocaleString()}
**Lifetime Value:** $${Number(selectedCustomer.lifetime_value || 0).toFixed(2)}
**VIP Status:** ${Number(selectedCustomer.vip_status) > 0 ? 'Active VIP' : 'Not VIP'}
${orderSummary}

When writing SQL for this customer, use \`WHERE o.ocustomerid = '${selectedCustomer.id}'\` or \`WHERE c.contactid = '${selectedCustomer.id}'\` or join on their email.

## Database Schema (Neon — use these exact column names)
customers (contactid TEXT PK, email, firstname, lastname, phone)
vip_memberships (id, customer_id INTEGER, joined_date, expiration_date, active BOOLEAN, auto_renew BOOLEAN, has_payment_profile BOOLEAN)
  -- JOIN: vm.customer_id::text = c.contactid (type cast required)
addresses (address_id PK, first_name, last_name, address TEXT, address2, city, state, zip, phone)
  -- JOIN via orders: orders.bill_to_id = addresses.address_id OR orders.ship_to_id = addresses.address_id
orders (orderid TEXT PK, bc_order_id TEXT, ocustomerid TEXT, bill_to_id, ship_to_id, odate TEXT, shipdate TEXT, oshipmethodtype TEXT, vatnumber TEXT, orderamount TEXT, oshipcost TEXT, coupon TEXT, coupondiscount TEXT)
  -- JOIN: o.ocustomerid = c.contactid
  -- NOTE: bc_order_id is THE order number staff use. Always select and show it (AS order_number); never output orderid, which is an internal key for joins only.
  -- NOTE: orderamount, oshipcost etc. are TEXT — cast with ::numeric for math
oitems (orderitemid, orderid, itemname TEXT, partnumber TEXT, numitems INTEGER, unitprice NUMERIC)
order_item_options (id, order_item_id, feature TEXT, value TEXT)

You have one tool: **run_sql** — write any SELECT query against these tables. Always filter to this customer unless the user asks a broader question.`;
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    setChatInput('');
    setChatLoading(true);
    setDisplayMessages(prev => [...prev, { role: 'user', text }]);
    let msgs = [...apiMessages, { role: 'user', content: text }];
    setApiMessages(msgs);
    try {
      let lastExportRows = null;
      while (true) {
        const res = await fetch(`${CHAT_BASE}/vip-chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: msgs, systemContext: buildSystemContext() }),
        });
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); }
        catch (e) {
          if (res.status >= 500 || /^<(!doctype|html)/i.test(text.trim().slice(0, 20))) {
            throw new Error(`Jimmy ran out of time (30-second limit). Your question was too large to answer in one shot — try asking for fewer rows (e.g. "top 20" instead of "top 50"), or break the question into smaller pieces.`);
          }
          throw new Error(`Unexpected response from Jimmy (HTTP ${res.status}).`);
        }
        if (data.error) throw new Error(data.detail?.error?.message || JSON.stringify(data.detail) || data.error);
        if (data.type === 'error' && data.error?.message) throw new Error(`Jimmy backend error: ${data.error.message}`);
        if (!Array.isArray(data.content)) {
          throw new Error(`Jimmy returned an unexpected response shape (no content array). ${JSON.stringify(data).slice(0, 200)}`);
        }
        if (data.stop_reason === 'tool_use') {
          msgs = [...msgs, { role: 'assistant', content: data.content }];
          const toolResults = [];
          for (const block of data.content) {
            if (block.type === 'tool_use') {
              const { result, exportRows } = await executeTool(block.name, block.input);
              if (exportRows?.length) lastExportRows = exportRows;
              toolResults.push({ type: 'tool_result', tool_use_id: block.id, content: result });
            }
          }
          msgs = [...msgs, { role: 'user', content: toolResults }];
        } else {
          msgs = [...msgs, { role: 'assistant', content: data.content }];
          setApiMessages(msgs);
          const reply = data.content.find(b => b.type === 'text')?.text ?? '';
          setDisplayMessages(prev => [...prev, { role: 'assistant', text: reply, exportRows: lastExportRows }]);
          break;
        }
      }
    } catch (e) {
      setDisplayMessages(prev => [...prev, { role: 'assistant', text: `Error: ${e.message}`, isError: true }]);
    } finally {
      setChatLoading(false);
    }
  };

  const openRewardsHistory = () => {
    if (!selectedCustomer?.bc_customer_id) {
      alert('No BigCommerce customer id on this record — cannot look up rewards history.');
      return;
    }
    setRewardsOpen(true);
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setStoreCredit(null);
    setRewardsOpen(false);
    setOrdersLoading(true);
    try {
      const rows = await neonQuery('orders_by_customer', { customerId: customer.id });
      setOrders(rows);
    } catch (e) {
      console.error('orders_by_customer failed:', e.message, 'customerId:', customer.id);
      setOrders([]);
    } finally {
      setOrdersLoading(false);
    }
    if (customer.email) {
      fetch('/.netlify/functions/bc-store-credit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: customer.email }),
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d && typeof d.storeCredit === 'number') setStoreCredit(d.storeCredit); })
        .catch(() => {});
    }
  };

  const loginAsCustomer = async () => {
    if (!selectedCustomer?.bc_customer_id) {
      alert('No BigCommerce customer id on this record — cannot log in as this user.');
      return;
    }
    const name = toTitleCase([selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ')) || selectedCustomer.email;
    if (!window.confirm(`Open a storefront session as ${name}? You'll be logged in as this customer in a new tab.`)) return;
    setLoginBusy(true);
    try {
      const res = await fetch('/.netlify/functions/bc-customer-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: selectedCustomer.bc_customer_id, sessionToken: getAccessToken() }),
      });
      const data = await res.json();
      if (!res.ok || !data.loginUrl) throw new Error(data.error || 'No loginUrl returned');
      window.open(data.loginUrl, '_blank', 'noopener');
    } catch (e) {
      alert(`Login-as failed: ${e.message}`);
    } finally {
      setLoginBusy(false);
    }
  };

  const search = async (query, forceSelect) => {
    if (!query || !query.trim()) return;
    setLoading(true);
    setError(null);
    setCustomers(null);
    setSelectedCustomer(null);
    setOrders(null);
    try {
      // A UPS tracking number finds its order, then the customer on that order.
      if (/^1Z/i.test(query.trim())) {
        const hit = await neonQuery('order_by_tracking', { q: query.trim().toUpperCase() });
        const byTracking = hit.length ? await neonQuery('customer_by_order', { q: hit[0].orderid }) : [];
        setCustomers(byTracking);
        if (byTracking.length === 1) selectCustomer(byTracking[0]);
        return;
      }
      // Try exact order/tracking lookup first
      const isNumeric = /^\d+$/.test(query.trim());
      if (isNumeric || query.includes('-')) {
        const byOrder = await neonQuery('customer_by_order', { q: query.trim() });
        if (byOrder.length > 0) {
          setCustomers(byOrder);
          if (byOrder.length === 1 || forceSelect) selectCustomer(byOrder[0]);
          return;
        }
      }
      const results = await neonQuery('customer_search', { q: query.trim() });
      setCustomers(results);
      if (results.length === 1 || (forceSelect && results.length > 0)) selectCustomer(results[0]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialQuery) {
      const v = initialQuery.email || (initialQuery.firstName && initialQuery.lastName ? `${initialQuery.firstName} ${initialQuery.lastName}` : initialQuery.firstName || initialQuery.lastName) || initialQuery.phoneNumber || initialQuery.trackingNum || initialQuery.invoiceNum || initialQuery.customerId || '';
      setQ(String(v));
      if (v) search(String(v), !!initialQuery.autoSelect);
    }
  }, [initialQuery]);

  // Linked-account chips for whatever is on screen (lib/linked-accounts.js).
  const linkIdsKey = [...(customers || []).map(c => c.id), selectedCustomer?.id].filter(Boolean).join(',');
  const refreshLinks = useCallback(() => {
    const ids = linkIdsKey ? linkIdsKey.split(',') : [];
    if (!ids.length) { setLinkMap({}); return; }
    neonQuery('identity_links_for_customers', { ids })
      .then(r => setLinkMap((r && r.links) || {})).catch(() => setLinkMap({}));
  }, [linkIdsKey]);
  useEffect(() => { refreshLinks(); }, [refreshLinks]);

  // Jump to a linked account without losing the thread: search its email, open that exact record.
  const openLinked = async (m) => {
    const key = m.email || String(m.contactId);
    setQ(key);
    try {
      const results = await neonQuery('customer_search', { q: key });
      const hit = (results || []).find(r => String(r.id) === String(m.contactId)) || (results || [])[0];
      setCustomers(results || []);
      if (hit) selectCustomer(hit);
    } catch (e) { setError(e.message); }
  };

  // ⚠️ "VIP · via linked account" only for SAME-PERSON links. A spouse's membership is theirs.
  const linkBadge = (c) => {
    const l = c && linkMap[c.id];
    if (!l) return null;
    return (
      <>
        {Number(c.vip_status) === 0 && l.linkedVip && (
          <span title="An account linked to this person is an active VIP" style={{ marginLeft: 6, fontSize: 9, background: 'var(--orange)', color: 'white', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>VIP · via linked account</span>
        )}
        <span title={`${l.linked} other account${l.linked === 1 ? '' : 's'} belong to this ${l.confirmed ? '' : 'likely '}person or household`} style={{ marginLeft: 6, fontSize: 9, background: '#e7eef4', color: 'var(--navy)', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }}>🔗 {l.linked} linked</span>
      </>
    );
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') search(q); };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Customer Search</h2>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Search</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>NAME, EMAIL, ORDER #, OR TRACKING #</div>
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search customers..."
              className="form-input"
              style={{ fontSize: 12, width: '100%' }}
              autoFocus
            />
          </div>
          <button onClick={() => search(q)} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
          <button onClick={() => { setQ(''); setCustomers(null); setSelectedCustomer(null); setOrders(null); }} className="btn btn-navy" style={{ fontSize: 11 }}>Clear</button>
        </div>
      </div>

      {error && <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
      {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Searching...</div>}

      {/* Customer list */}
      {!loading && customers !== null && !selectedCustomer && (
        <div className="card">
          <div className="card-header">
            {customers.length === 0 ? 'No customers found' : `${customers.length} customer${customers.length !== 1 ? 's' : ''} found`}
          </div>
          {customers.length > 0 && (
            <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {customers.map(c => (
                <div key={c.id}
                  onClick={() => selectCustomer(c)}
                  style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '10px 14px', border: '1px solid var(--gray-200)', borderRadius: 6, cursor: 'pointer', background: '#fff' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--gray-50)'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--navy)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                    {c.first_name ? c.first_name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13 }}>
                      {toTitleCase([c.first_name, c.last_name].filter(Boolean).join(' ') || '—')}
                      {Number(c.vip_status) > 0 && <VipBadge notRenewing={Number(c.vip_not_renewing) > 0} style={{ marginLeft: 6, fontSize: 9, background: 'var(--orange)', color: 'white', padding: '1px 6px', borderRadius: 8, fontWeight: 700 }} />}
                      {linkBadge(c)}
                    </div>
                    <div style={{ color: 'var(--orange)', fontSize: 12 }}>{c.email}</div>
                    {c.phone && <div style={{ color: 'var(--gray-500)', fontSize: 11 }}>{c.phone}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, fontSize: 11 }}>
                    <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 13 }}>{Number(c.order_count).toLocaleString()}</div>
                    <div style={{ color: 'var(--gray-500)' }}>order{Number(c.order_count) !== 1 ? 's' : ''}</div>
                    {Number(c.lifetime_value) > 0 && (
                      <div style={{ color: 'var(--green)', fontWeight: 600, marginTop: 2 }}>{fmtCurrency(c.lifetime_value)}</div>
                    )}
                    <div style={{ color: 'var(--gray-400)', marginTop: 2 }}>Last: {fmtDate(c.last_order_date)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selected customer detail + orders */}
      {selectedCustomer && (
        <div>
          {/* Customer header card */}
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <a onClick={() => { setSelectedCustomer(null); setOrders(null); }}
                style={{ cursor: 'pointer', color: 'var(--orange)', fontSize: 12, fontWeight: 600 }}>← Back</a>
              <span>Customer #{selectedCustomer.id}</span>
            </div>
            <div style={{ padding: '12px 16px', fontSize: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 15 }}>
                      {toTitleCase([selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ') || '—')}
                      {Number(selectedCustomer.vip_status) > 0 && <VipBadge notRenewing={Number(selectedCustomer.vip_not_renewing) > 0} style={{ marginLeft: 8, fontSize: 9, background: 'var(--orange)', color: 'white', padding: '1px 7px', borderRadius: 8, fontWeight: 700 }} />}
                      {linkBadge(selectedCustomer)}
                    </div>
                    <div style={{ color: 'var(--orange)', marginTop: 2 }}>{selectedCustomer.email}</div>
                    {selectedCustomer.phone && <div style={{ color: 'var(--gray-500)', marginTop: 2 }}>{selectedCustomer.phone}</div>}
                    <ListStatus contactIds={[String(selectedCustomer.id)]} email={selectedCustomer.email} style={{ marginTop: 6 }} />
                  </div>
                  <div style={{ borderLeft: '1px solid var(--gray-200)', paddingLeft: 16, display: 'flex', gap: 20, alignItems: 'center' }}>
                    <div className="history-stat"><label>Total Orders:&nbsp;</label><span>{Number(selectedCustomer.order_count).toLocaleString()}</span></div>
                    <div className="history-stat"><label>Lifetime Value:&nbsp;</label><span style={{ color: 'var(--green)' }}>{fmtCurrency(selectedCustomer.lifetime_value)}</span></div>
                    <div className="history-stat"><label>Last Order:&nbsp;</label><span>{fmtDate(selectedCustomer.last_order_date)}</span></div>
                    <div
                      className="history-stat"
                      onClick={openRewardsHistory}
                      style={{ cursor: selectedCustomer.bc_customer_id ? 'pointer' : 'default' }}
                      title={selectedCustomer.bc_customer_id ? 'Click to view reward history' : ''}
                    >
                      <label style={{ cursor: 'inherit' }}>VIP Cash Rewards:&nbsp;</label>
                      <span style={{
                        color: storeCredit && storeCredit > 0 ? 'var(--green)' : 'var(--gray-500)',
                        textDecoration: selectedCustomer.bc_customer_id ? 'underline dotted' : 'none',
                        textUnderlineOffset: 3,
                      }}>
                        {storeCredit === null ? '…' : fmtCurrency(storeCredit)}
                      </span>
                    </div>
                    <a href={`/?page=customer-analysis&q=${encodeURIComponent(selectedCustomer.email || selectedCustomer.id)}`}
                      target="_blank" rel="noopener noreferrer"
                      title="This customer's whole history: spend by year, what they buy, problems on file, every order"
                      style={{ fontSize: 12, fontWeight: 700, color: 'var(--orange)', whiteSpace: 'nowrap' }}>
                      Customer analysis report ↗
                    </a>
                  </div>
                </div>
                {canLoginAsCustomer && (
                  <button
                    onClick={loginAsCustomer}
                    disabled={loginBusy || !selectedCustomer.bc_customer_id}
                    className="btn btn-navy"
                    title={selectedCustomer.bc_customer_id ? 'Open a storefront session as this customer (new tab)' : 'No BigCommerce customer id on record'}
                    style={{ fontSize: 11, whiteSpace: 'nowrap', opacity: (loginBusy || !selectedCustomer.bc_customer_id) ? 0.6 : 1 }}
                  >
                    {loginBusy ? 'Opening…' : 'Login as customer ↗'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Linked accounts: same person / household. Renders only when this account is in a group. */}
          <LinkedAccountsCard customerId={selectedCustomer.id} onOpen={openLinked} navigateTo={navigateTo} onChanged={refreshLinks} />

          {/* Social media notes — attached FB/IG comments; renders only when present */}
          <SocialNotes customerId={selectedCustomer.id} />

          {/* Contact History */}
          <ContactHistory phone={selectedCustomer.phone} email={selectedCustomer.email} />

          {/* Address Book */}
          <AddressBook bcCustomerId={selectedCustomer.bc_customer_id} />

          {/* Payment Profile */}
          <PaymentProfile customerId={selectedCustomer.id} customerProfileId={selectedCustomer.customer_profile_id} />

          {/* Orders table */}
          <div className="card">
            <div className="card-header">Order History</div>
            {ordersLoading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading orders...</div>}
            {!ordersLoading && orders !== null && orders.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No orders found.</div>
            )}
            {!ordersLoading && orders && orders.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order #</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Method</th>
                      <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Tracking</th>
                      <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((row, i) => (
                      <tr key={i}
                        style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}
                        onClick={() => navigateTo && navigateTo('order-details', String(row.bc_order_id ?? row.id))}
                      >
                        <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bc_order_id ?? row.id ?? '—'}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{fmtDate(row.order_date)}</td>
                        <td style={{ padding: '8px 8px' }}>
                          <span className={`order-status-badge status-${(row.canceled === '1' ? 'canceled' : row.done === '1' || row.done === true ? 'shipped' : row.shipdate ? 'shipping' : 'processing').toLowerCase()}`}>
                            {row.canceled === '1' ? 'Cancelled' : row.done === '1' || row.done === true ? 'Shipped' : row.shipdate ? 'Shipping' : 'Processing'}
                          </span>
                        </td>
                        <td style={{ padding: '8px 8px', color: 'var(--gray-700)' }}>{row.ship_method ?? '—'}</td>
                        <td style={{ padding: '8px 8px', color: 'var(--gray-500)', fontSize: 11 }}>{row.tracking_number ?? '—'}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{fmtCurrency(row.order_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* VIP Cash Rewards history modal */}
      <VipRewardsModal
        open={rewardsOpen && !!selectedCustomer}
        onClose={() => setRewardsOpen(false)}
        bcCustomerId={selectedCustomer?.bc_customer_id}
        customerName={selectedCustomer ? (toTitleCase([selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(' ')) || selectedCustomer.email) : ''}
        storeCredit={storeCredit}
        showDrift={true}
      />

      {/* ── Jimmy chat ── always visible ── */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setChatOpen(o => !o)}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <img src={jimmyImg} alt="Jimmy" style={{ width: 48, height: 48, objectFit: 'contain' }} />
            {selectedCustomer ? 'Ask Jimmy about this customer' : 'Ask Jimmy about customers'}
          </span>
          <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{chatOpen ? '▲' : '▼'}</span>
        </div>
        {chatOpen && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ maxHeight: 360, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {displayMessages.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--gray-400)', textAlign: 'center', padding: '16px 0' }}>
                  {selectedCustomer
                    ? 'Ask anything about this customer — order history, spending patterns, product preferences, and more.'
                    : 'Ask anything about your customers — how many in a city, top spenders, purchase trends, and more.'}
                </div>
              )}
              {displayMessages.map((msg, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 8 }}>
                  {msg.role === 'assistant' && <img src={jimmyImg} alt="Jimmy" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />}
                  <div style={{ maxWidth: '80%', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ padding: '8px 12px', borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px', background: msg.role === 'user' ? 'var(--orange)' : (msg.isError ? '#fef3f0' : 'var(--gray-50)'), color: msg.role === 'user' ? '#fff' : (msg.isError ? 'var(--red)' : 'var(--navy)'), fontSize: 12, lineHeight: 1.5, border: msg.role === 'assistant' ? '1px solid var(--gray-100)' : 'none', whiteSpace: 'pre-wrap' }}>
                      {msg.text}
                    </div>
                    {msg.exportRows?.length > 0 && (
                      <button onClick={() => downloadCsv(msg.exportRows)} style={{
                        alignSelf: 'flex-start', background: 'none', border: '1px solid var(--gray-300)',
                        borderRadius: 6, padding: '3px 9px', fontSize: 11, color: 'var(--gray-600)',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                      }}>
                        ↓ Export CSV ({msg.exportRows.length} rows)
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', gap: 8 }}>
                  <img src={jimmyImg} alt="Jimmy" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />
                  <div style={{ padding: '8px 14px', borderRadius: '12px 12px 12px 2px', background: 'var(--gray-50)', border: '1px solid var(--gray-100)', fontSize: 18, letterSpacing: 2, color: 'var(--gray-400)' }}>···</div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '10px 16px', borderTop: '1px solid var(--gray-100)' }}>
              <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()} placeholder={selectedCustomer ? 'Ask about this customer...' : 'Ask about customers...'} className="form-input" style={{ flex: 1, fontSize: 12 }} disabled={chatLoading} />
              <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()} className="btn btn-primary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>Send</button>
              {displayMessages.length > 0 && (
                <button onClick={() => { setDisplayMessages([]); setApiMessages([]); }} className="btn btn-navy" style={{ fontSize: 12 }}>Clear</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
